'use client';

import { cva } from 'class-variance-authority';
import { CircleX, Info } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Input } from './input';

const fieldVariants = cva(
  'relative flex min-h-[56px] w-full items-stretch rounded-xl border bg-background-fill-primary transition-colors',
  {
    variants: {
      leading: {
        true: 'gap-0 pr-2',
        false: 'gap-2 px-4',
      },
      invalid: {
        true: 'border-brand-red-300 focus-within:border-brand-red-300',
        false:
          'border-border hover:border-brand-grey2-400 hover:bg-background-fill-primary-hover focus-within:border-brand-green2-300 focus-within:bg-background-fill-primary',
      },
      disabled: {
        true: 'pointer-events-none opacity-50',
        false: '',
      },
    },
    defaultVariants: { leading: false, invalid: false, disabled: false },
  },
);

const FieldDescription = ({
  id,
  invalid,
  children,
}: {
  id: string;
  invalid: boolean;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-1">
    {invalid ? (
      <CircleX
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-text-danger"
      />
    ) : (
      <Info
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-text-subtle"
      />
    )}
    <p
      aria-live={invalid ? 'assertive' : undefined}
      className={cn(
        'text-sm leading-[22px]',
        invalid ? 'text-text-danger' : 'text-text-subtle',
      )}
      id={id}
      role={invalid ? 'alert' : undefined}
    >
      {children}
    </p>
  </div>
);

export interface InputFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'> {
  /** Floating label — also acts as the placeholder when empty. */
  label: string;
  /** Helper text under the field; replaced by `error` when validation fails. */
  hint?: string;
  /** Error message; its presence switches the field into the error state. */
  error?: string;
  /** Forces the error state without a message (e.g. an external validity flag). */
  invalid?: boolean;
  /** Trailing icon (e.g. password toggle, select chevron). */
  trailingIcon?: React.ReactNode;
  /** Makes the trailing icon an accessible button. */
  onTrailingIconClick?: () => void;
  /** Accessible label for the trailing icon button (required when clickable). */
  trailingIconLabel?: string;
  /** Emoji shortcuts shown in a floating bubble above the field while it has focus. */
  emojiSuggestions?: readonly string[];
  /** Called with the picked emoji; the consumer decides how to apply it to the value. */
  onEmojiSelect?: (emoji: string) => void;
  /** Accessible name for the emoji shortcut group (required with `emojiSuggestions`). */
  emojiSuggestionsLabel?: string;
  /** Classes for the outer wrapper (field + footer). */
  containerClassName?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      label,
      hint,
      error,
      invalid = false,
      required = false,
      disabled = false,
      trailingIcon,
      onTrailingIconClick,
      trailingIconLabel,
      emojiSuggestions,
      onEmojiSelect,
      emojiSuggestionsLabel,
      placeholder,
      className,
      containerClassName,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const fieldId = id ?? generatedId;
    const descriptionId = `${fieldId}-description`;

    const innerRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    const isInvalid = invalid || Boolean(error);
    const description = error ?? hint;
    const hasIcon = Boolean(trailingIcon);
    const hasEmojiSuggestions =
      !disabled && Boolean(emojiSuggestions?.length) && Boolean(onEmojiSelect);

    const describedBy = description ? descriptionId : undefined;

    const handleEmojiSelect = (emoji: string) => {
      onEmojiSelect?.(emoji);
      // Bring the caret back to the input so the bubble stays open and the user
      // can keep typing right after the inserted emoji.
      innerRef.current?.focus();
    };

    return (
      <div
        className={cn(
          'group/field flex w-full flex-col gap-1',
          containerClassName,
        )}
      >
        <div className={cn(fieldVariants({ invalid: isInvalid, disabled }))}>
          {hasEmojiSuggestions && (
            // Floating bubble above the field, aligned to its left edge. It is
            // absolutely positioned on purpose: it overlays whatever sits above
            // instead of pushing the form layout when it appears.
            <div
              aria-label={emojiSuggestionsLabel}
              className="-left-px absolute bottom-[calc(100%+7px)] z-20 hidden w-fit items-center gap-3 rounded-2xl border border-border bg-background-surface-primary px-4 py-1.5 shadow-[0_0_12px_2px_var(--color-brand-grey2-300)] group-focus-within/field:flex"
              role="group"
            >
              {emojiSuggestions?.map((emoji) => (
                <button
                  className="flex size-9 shrink-0 items-center justify-center rounded-full text-[17px] leading-none transition-colors hover:bg-background-fill-primary-hover"
                  key={emoji}
                  onClick={() => handleEmojiSelect(emoji)}
                  // Keep the field focused on pointer interaction so the bubble
                  // does not collapse before the click lands.
                  onMouseDown={(event) => event.preventDefault()}
                  type="button"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
          <div className="relative min-w-0 flex-1">
            <Input
              {...props}
              aria-describedby={describedBy}
              aria-invalid={isInvalid || undefined}
              aria-required={required || undefined}
              className={cn(
                'peer h-full truncate pt-6 pb-1 text-base leading-6 text-text placeholder:text-transparent focus:placeholder:text-text-subtle',
                isInvalid && 'text-text-danger',
                className,
              )}
              disabled={disabled}
              id={fieldId}
              placeholder={placeholder ?? ' '}
              ref={innerRef}
              variant="ghost"
            />
            <label
              className={cn(
                'pointer-events-none absolute top-2 right-0 left-0 truncate text-sm leading-[22px] transition-all duration-200',
                'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6',
                'peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-sm peer-focus:leading-[22px]',
                isInvalid ? 'text-text-danger' : 'text-text-subtle',
              )}
              htmlFor={fieldId}
            >
              {label}
              {required ? ' *' : ''}
            </label>
          </div>

          {hasIcon &&
            (onTrailingIconClick ? (
              <button
                aria-label={trailingIconLabel}
                className="shrink-0 self-center text-text-subtle"
                disabled={disabled}
                onClick={onTrailingIconClick}
                type="button"
              >
                {trailingIcon}
              </button>
            ) : (
              <span
                aria-hidden="true"
                className="shrink-0 self-center text-text-subtle"
              >
                {trailingIcon}
              </span>
            ))}
        </div>

        {description && (
          <FieldDescription id={descriptionId} invalid={isInvalid}>
            {description}
          </FieldDescription>
        )}
      </div>
    );
  },
);
InputField.displayName = 'InputField';

export { InputField, FieldDescription, fieldVariants };
